import type { ReactElement } from 'react';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Icon, Link, Modal, tokens, useCopyToClipboard } from '@hotjar/hotsauce';
import { Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { useQuery } from 'react-query';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import { generateMfaSettings } from '@hotjar/webapp/domains/mfa/api';
import type { GenerateMfaSettingsResponseModel } from '@hotjar/webapp/domains/mfa/model';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { MfaRecoveryCodeRef } from '@hotjar/webapp/features/Mfa/components';
import {
  MfaCodeVerification,
  MfaQRCode,
  MfaRecoveryCode,
} from '@hotjar/webapp/features/Mfa/components';
import { useMfaCodeVerification } from '@hotjar/webapp/hooks/mfa';
import { useMfaModals } from '@hotjar/webapp/features/Mfa/useMfaModals';
import { VerifyEmailModalContents } from './VerifyEmailModalContents';
import { PromptVerifyEmailContents } from './PromptVerifyEmailContents';

interface MfaEnableModalProps {
  userEmail: string;
  onClickChangeEmail: () => void;
  userId: number;
  isEmailVerified: boolean;
  onEmailVerified: () => void;
  onEnabled: () => void;
}

interface MfaQRCodeModalProps {
  uri: string;
  hasErrors: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface MfaOTPModalProps {
  userId: number;
  onClose: () => void;
  onEnabled: () => void;
}

interface MfaRecoveryCodesModalProps {
  recoveryCodes: string[];
}

const StyledDiv = styled.div`
  color: ${tokens.color.text_success};
  margin-bottom: ${tokens.spacer.x2};
`;

const StyledIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
`;

export const MfaRecoveryCodesModal = ({
  recoveryCodes,
}: MfaRecoveryCodesModalProps): ReactElement => {
  const readableRecoveryCodes = recoveryCodes.join('\n');
  const { t } = useTranslationWithPrefix('mfa:enable-modal.recovery-codes');
  const { isCopied, copy } = useCopyToClipboard(readableRecoveryCodes);

  const recoveryCodeRef = useRef<MfaRecoveryCodeRef>(null);

  const onPrint = () => {
    recoveryCodeRef?.current?.print();
  };

  return (
    <>
      <Modal.Body>
        <StyledDiv>
          <StyledIcon icon="check" inline />
          {t('success-message')}
        </StyledDiv>
        <p>{t('description')}</p>
        <Trans i18nKey="mfa:enable-modal:recovery-codes:instructions">
          <u>print them</u>
        </Trans>
        <MfaRecoveryCode recoveryCodes={recoveryCodes} ref={recoveryCodeRef} />
      </Modal.Body>
      <Modal.Footer>
        <Button kind="secondary" onClick={copy}>
          {isCopied ? t('copied') : t('copy')}
        </Button>
        <Button kind="primary" onClick={onPrint}>
          {t('print')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export const MfaOTPModal = ({ userId, onClose, onEnabled }: MfaOTPModalProps): ReactElement => {
  const { t } = useTranslationWithPrefix('mfa:enable-modal.otp-code');
  const { handleChange, handleInput, submit, isLoading, error } = useMfaCodeVerification(
    userId,
    onEnabled,
  );

  return (
    <>
      <Modal.Body>
        <p>{t('description')}</p>
        <MfaCodeVerification error={error} onChange={handleChange} onInput={handleInput} />
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          kind="primary"
          isLoading={isLoading}
          isDisabled={!!error}
          onClick={submit}
          type="submit"
        >
          {t('action')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export const MfaQRCodeModal = ({
  uri,
  hasErrors,
  isLoading,
  onClose,
  onConfirm,
}: MfaQRCodeModalProps): ReactElement => {
  const { t } = useTranslationWithPrefix('mfa:enable-modal.qr-code');
  const helpHotjarUrl = useHelpHotjarUrl();

  return (
    <>
      <Modal.Body>
        <p>{t('description')}</p>
        <Trans i18nKey="mfa:enable-modal:qr-code:instructions">
          <Link href={`${helpHotjarUrl}/articles/4406998019223`} isExternal kind="inline">
            any of these supported apps
          </Link>
        </Trans>
        <MfaQRCode uri={uri} hasErrors={hasErrors} isLoading={isLoading} />
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button kind="primary" isDisabled={hasErrors} onClick={onConfirm} type="submit">
          {t('action')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export const MfaEnableModal = ({
  userEmail,
  userId,
  onClickChangeEmail,
  isEmailVerified,
  onEmailVerified,
  onEnabled,
}: MfaEnableModalProps): ReactElement => {
  const [currentStep, setCurrentStep] = useState(''); // First step is determined in the useEffect() for isOpen
  const { enableMfa: isOpen, setEnableMfa } = useMfaModals();
  const onClose = useCallback(() => setEnableMfa(false), [setEnableMfa]);

  // Fetcher for new QR code and recovery codes
  const {
    data: mfaSettings,
    isFetching,
    status,
  } = useQuery<GenerateMfaSettingsResponseModel>(
    ['mfa-settings', userId],
    () => generateMfaSettings(userId),
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: !!userId && !!isOpen && currentStep === 'qrCode',
    },
  );

  const { t } = useTranslationWithPrefix('mfa:enable-modal');
  const { loadedModal } = useTrack();

  const modalHeader = (step: string): string => {
    switch (step) {
      case 'promptVerifyEmail':
        return t('prompt-verify-email.title');
      case 'verifyEmail':
        return t('verify-email.title');
      default:
        return t('title');
    }
  };

  const handleOnNext = useCallback(() => {
    setCurrentStep('qrCode');
  }, [setCurrentStep]);

  const steps: Record<string, Record<string, () => ReactElement | undefined>> = useMemo(
    () => ({
      promptVerifyEmail: {
        render: () => {
          return (
            <PromptVerifyEmailContents
              onClose={onClose}
              onProceed={() => setCurrentStep('verifyEmail')}
            />
          );
        },
      },
      verifyEmail: {
        render: () => {
          return (
            <VerifyEmailModalContents
              onClickChangeEmail={onClickChangeEmail}
              userEmail={userEmail}
              onEmailVerified={onEmailVerified}
              onNext={handleOnNext}
              onClose={onClose}
            />
          );
        },
      },
      qrCode: {
        render: () =>
          mfaSettings && (
            <MfaQRCodeModal
              isLoading={isFetching}
              uri={mfaSettings.uri}
              hasErrors={status === 'error'}
              onClose={onClose}
              onConfirm={() => setCurrentStep('otp')}
            />
          ),
      },
      otp: {
        render: () => {
          // Handles "MFA enabled" event
          const handleMFAEnabled = () => {
            setCurrentStep('recCodes');
            onEnabled();
          };

          return (
            mfaSettings && (
              <MfaOTPModal userId={userId} onClose={onClose} onEnabled={handleMFAEnabled} />
            )
          );
        },
      },
      recCodes: {
        render: () =>
          mfaSettings && <MfaRecoveryCodesModal recoveryCodes={mfaSettings.recoveryCodes} />,
      },
    }),
    [
      onEnabled,
      onClose,
      userId,
      mfaSettings,
      handleOnNext,
      onEmailVerified,
      status,
      isFetching,
      onClickChangeEmail,
      userEmail,
    ],
  );

  // Define behaviour for when modal opens
  useEffect(() => {
    if (isOpen) {
      loadedModal('Enable MFA', undefined, {});
      // Determine the first step
      setCurrentStep(isEmailVerified ? 'qrCode' : 'promptVerifyEmail');
    }
  }, [isOpen, loadedModal, isEmailVerified]);

  return (
    <Modal contentLabel={t('content-label')} isOpen={isOpen} onClose={onClose}>
      {currentStep && <Modal.Header title={modalHeader(currentStep)} />}
      {currentStep && steps[currentStep].render()}
    </Modal>
  );
};
